import React, { useEffect, useState, useRef } from "react";
import "../../assets/css/Services.css";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

// meta data
import { Helmet } from "react-helmet";
import data from "../../component/ApiUrl/meta_api";

/* ─── Data ─────────────────────────────────────────────────────────────────── */
const cards = [
  {
    icon: "fa-solid fa-screwdriver-wrench",
    title: "Automation System Design & Engineering",
    para: "We design and engineer end-to-end industrial automation systems tailored to your production needs maximizing efficiency, accuracy, and uptime.",
    list: [
      "Custom Automation Architecture",
      "Process Flow Optimization",
      "System Integration & Commissioning",
      "On-Site Installation & Support",
    ],
  },

  {
    icon: "fa-solid fa-sliders",
    title: "SCADA & Process Control Systems",
    para: "Gain complete visibility and control over your industrial operations with our advanced SCADA and process control solutions built for real-time performance.",
    list: [
      "Real-Time Data Monitoring",
      "SCADA System Development",
      "Process Control & Alarming",
      "Remote Access & Control",
    ],
  },

  {
    icon: "fa-solid fa-robot",
    title: "Robotic Solutions & PLC Programming",
    para: "Harness the power of industrial robotics and programmable logic controllers to automate repetitive tasks, reduce errors and boost production throughput.",
    list: [
      "Industrial Robot Integration",
      "PLC Programming & Commissioning",
      "Motion Control Systems",
      "Multi-Brand PLC Support",
    ],
  },

  {
    icon: "fa-solid fa-gears",
    title: "Panel Fabrication & Control Systems",
    para: "From design to delivery, we fabricate high-quality electrical control panels built to industry standards ensuring safe, reliable and efficient operations.",
    list: [
      "MCC & PCC Panel Fabrication",
      "Control Panel Design & Wiring",
      "HMI Integration",
      "Testing & Quality Certification",
    ],
  },

  {
    icon: "fa-solid fa-bolt",
    title: "Energy Management System (EMS)",
    para: "Optimize your energy consumption and reduce operational costs with our intelligent energy management systems designed for modern industrial environments.",
    list: [
      "Energy Monitoring & Analysis",
      "Power Quality Management",
      "Load Balancing & Optimization",
      "Renewable Energy Integration",
    ],
  },

  {
    icon: "fa-solid fa-chalkboard-user",
    title: "Training & Technical Support Services",
    para: "Empower your workforce with hands-on industrial automation training and round-the-clock technical support to keep your operations running at peak performance.",
    list: [
      "PLC & SCADA Training Programs",
      "On-Site Technical Support",
      "Annual Maintenance Contracts (AMC)",
      "Breakdown & Repair Services",
    ],
  },
];

const STEPS = [
  {
    icon: "fa-magnifying-glass",
    title: "Requirement Analysis & Planning",
    desc: "Analyze industrial processes and machines to identify automation opportunities and system requirements.",
  },
  {
    icon: "fa-gears",
    title: "Automation System Design",
    desc: "Design PLC architecture, control systems, and automation workflows for industrial applications.",
  },
  {
    icon: "fa-microchip",
    title: "PLC Programming & Integration",
    desc: "Develop PLC programs and integrate systems with sensors, SCADA, HMI, and networks.",
  },
  {
    icon: "fa-desktop",
    title: "SCADA & HMI Implementation",
    desc: "Implement SCADA systems and HMI interfaces for real-time monitoring and control.",
  },
  {
    icon: "fa-gauge-high",
    title: "Monitoring & Optimization",
    desc: "Enable real-time analytics, predictive maintenance, and optimize system performance efficiently.",
  },
];

const FAQ_DATA = [
  [
    "What Industrial Automation services do you offer?",
    "We provide complete industrial automation services including PLC programming, SCADA systems, HMI development, IIoT integration, machine automation, and real-time monitoring solutions for smart manufacturing.",
  ],
  [
    "How can industrial automation improve my business?",
    "Industrial automation improves productivity, reduces manual work, minimizes errors, and enables real-time monitoring, helping businesses increase efficiency and reduce operational costs.",
  ],
  [
    "Do you provide PLC programming services?",
    "Yes, we offer advanced PLC programming services for machine control, process automation, and seamless integration with industrial systems and equipment.",
  ],
  [
    "What is included in SCADA and HMI solutions?",
    "Our SCADA and HMI solutions include real-time data monitoring, process visualization, control systems, alarm management, and remote access for industrial operations.",
  ],
  [
    "Do you offer IIoT integration in automation systems?",
    "Yes, we integrate Industrial IoT (IIoT) solutions to enable machine connectivity, cloud integration, real-time data analytics, and smart factory transformation.",
  ],
  [
    "Which industries do you support with automation solutions?",
    "We serve industries such as manufacturing, energy, automotive, water treatment, and infrastructure with customized industrial automation solutions.",
  ],
  [
    "How long does it take to implement an automation system?",
    "The timeline depends on project complexity. Small automation projects may take weeks, while large-scale industrial automation systems may take several months.",
  ],
  [
    "Do you provide real-time monitoring and analytics?",
    "Yes, we implement real-time monitoring dashboards and analytics systems to track machine performance, production data, and system efficiency.",
  ],
  [
    "Are your automation systems scalable and secure?",
    "Yes, we design secure and scalable automation systems using industry-standard protocols to ensure reliable performance and future expansion.",
  ],
  [
    "How can I get started with your Industrial Automation services?",
    "You can contact us for a free consultation. We analyze your requirements and provide a customized industrial automation solution tailored to your business needs.",
  ],
];

/* ─── Small components ──────────────────────────────────────────────────────── */
function SectionLabel({ text }) {
  return (
    <p
      className="fw-semibold text-uppercase mb-1"
      style={{ fontSize: 20, color: "#cc0000", letterSpacing: 1 }}
    >
      {text}
    </p>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      className={`rs-faq-item rs-anim${open ? " open" : ""}`}
      style={{ transitionDelay: `${idx * 55}ms` }}
    >
      <button className="rs-faq-btn" onClick={() => setOpen((o) => !o)}>
        <span className="rs-faq-q">{q}</span>
        <span className="rs-faq-icon">
          <i className="fa-solid fa-plus" />
        </span>
      </button>
      <div className="rs-faq-body">{a}</div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
export default function IAService() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);

  // scroll top
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const fetchData = () => {
    const IndustrialAutomationMeta = data[0]?.industrial_automation_page;
    if (IndustrialAutomationMeta) {
      setMeta(IndustrialAutomationMeta);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: "ease-in-out",
    });

    if (!document.getElementById("rs-styles")) {
      const s = document.createElement("style");
      s.id = "rs-styles";
      document.head.appendChild(s);
    }
    if (!document.querySelector('link[href*="font-awesome"]')) {
      const fa = document.createElement("link");
      fa.rel = "stylesheet";
      fa.href =
        "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css";
      document.head.appendChild(fa);
    }
    if (!document.querySelector('link[href*="Poppins"]')) {
      const gf = document.createElement("link");
      gf.rel = "stylesheet";
      gf.href =
        "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap";
      document.head.appendChild(gf);
    }
  }, []);

  // const tab = TABS[activeTab];

  return (
    <div className="body">
      <Helmet>
        <title>{meta ? meta.title : "Industrial Automation"}</title>
      </Helmet>

      {/* ═════════
          1. HERO 
      ═════════════ */}
      <div
        className="backgroundCommenImage position-relative d-flex align-items-center mb-5"
        style={{
          backgroundImage: `url(${require("../../assets/img/Service/Industrial Automation/banner.jpg")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "85vh",
        }}
      >
        {/* Overlay */}
       
      </div>

      {/* ════════════
          2. INTRO 
      ═════════════════ */}
      <div className="container margin_top container_width">
        <div className="row align-items-center justify-content-center g-5">
          {/* Left */}
          <div className="col-lg-6" data-aos="fade-right" data-aos-delay="100">
            <SectionLabel text="Industrial Automation Solutions for Smart Manufacturing" />

            <h2 className="fw-semibold mb-3" style={{ fontSize: 20 }}>
              Advanced Industrial Automation Solutions for Smart Manufacturing
            </h2>

            <p className="mb-3 content-text">
              <span className="fw-semibold">Skimitar Hindustan Pvt Ltd</span>{" "}
              provide advanced industrial automation solutions including PLC
              programming, SCADA integration, HMI development, IIoT
              connectivity, and real-time monitoring to improve efficiency,
              reduce downtime, and enable smart manufacturing.
            </p>

            <p className="mb-3 content-text">
              In today's Industry 4.0 environment, businesses need smart
              automation systems for real-time monitoring, process control, and
              data-driven decision making.
            </p>

            <ul className="list-unstyled content-list mt-3">
              {[
                "PLC Programming and SCADA Integration for advanced industrial automation systems",
                "HMI Development for intuitive machine control and real-time process monitoring",
                "Industrial Automation Solutions to improve manufacturing efficiency and productivity",
                "IIoT Integration for smart factory connectivity and industrial data management",
              ].map((item, i) => (
                <li
                  key={i}
                  className="d-flex align-items-start gap-2 mb-2 small"
                  data-aos="fade-up"
                  data-aos-delay={`${100 + i * 80}`}
                >
                  <i className="fa-solid fa-check text-danger mt-1"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right */}
          <div
            className="col-lg-6 text-center"
            data-aos="fade-right"
            data-aos-delay="200"
          >
            <div className="position-relative d-inline-block w-100">
              <img
                className="img-fluid rounded-4"
                src={require("../../assets/img/Service/Industrial Automation/img.png")}
                alt="Industrial Automation Services PLC SCADA HMI"
              />

              {/* Stat */}
              <div className="retro-stat">
                <i className="fa-solid fa-industry text-red fs-5"></i>
                <div>
                  <div className="fw-bold fs-5">150+</div>
                  <div className="small text-muted">
                    Industrial Automation Projects Delivered
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          3. SERVICE CARDS
      ══════════════════════════════════════════════════════════════ */}
      <section className="margin_top pb-5">
        <div className="container container_width">
          <div className="mb-1" data-aos="fade-up">
            <SectionLabel text="Our Industrial Automation Services" />
          </div>

          <div className="row mb-4">
            <div className="col-lg-7" data-aos="fade-up" data-aos-delay="100">
              <h2 className="fw-semibold mb-2" style={{ fontSize: 20 }}>
                Advanced Industrial Automation Solutions for Smart Manufacturing
              </h2>

              <p className="mb-3 content-text">
                From PLC programming to SCADA integration and IIoT-enabled
                automation, we deliver complete industrial automation solutions
                that improve efficiency, enable real-time monitoring, and
                support smart factory transformation.
              </p>
            </div>

            <div
              className="col-lg-5 text-lg-end mt-3 mt-lg-0"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <a href="#contact" className="btn btn-red px-4 py-2">
                Get Industrial Solution
                <i className="fa-solid fa-arrow-right ms-2"></i>
              </a>
            </div>
          </div>

          {/* Content */}
          <div className="row g-3">
            {cards.map((s, i) => (
              <div
                key={i}
                className="col-12 col-sm-6 col-lg-4"
                data-aos="fade-up"
                data-aos-delay={`${i * 100}`}
              >
                <div className="card h-100 border-0 shadow service-card">
                  <div className="card-body position-relative p-4">
                    {/* Number */}
                    {/* <span className="service-number">
                      {String(i + 1).padStart(2, "0")}
                    </span> */}

                    {/* Icon */}
                    <div className="service-icon mb-3">
                      <i className={`fa-solid ${s.icon}`}></i>
                    </div>

                    {/* title */}
                    <h5
                      className="fw-bold mb-2 text-red"
                      style={{ fontSize: 15 }}
                    >
                      {s.title}
                    </h5>

                    {/* para */}
                    <p className="small text-muted mb-2">{s.para}</p>

                    {/* list */}
                    <ul className="list-unstyled">
                      {s.list.map((item, i) => (
                        <li
                          key={i}
                          className="d-flex align-items-start gap-2 mb-2 small"
                          data-aos="fade-up"
                          data-aos-delay={`${100 + i * 80}`}
                        >
                          <i className="fa-solid fa-check text-danger mt-1"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <button className="btn btn-red btn-sm mt-2">
                      Enquire Now
                      <i className="fa-solid fa-arrow-right ms-2"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          4. HOW IT WORKS 5-step process
      ══════════════════════════════════════════════════════════════ */}
      <section className="margin_top py-5 bg-light">
        <div className="container container_width">
          <div className="mb-2" data-aos="fade-up">
            <SectionLabel text="How It Works" />
          </div>

          <div className="mb-5" data-aos="fade-up" data-aos-delay="100">
            <h2 className="fw-semibold mb-3" style={{ fontSize: 20 }}>
              Our Industrial Automation Implementation Process
            </h2>

            <p className="mb-3" style={{ fontSize: 15 }}>
              We follow a structured industrial automation process to deliver
              reliable, scalable, and high-performance solutions. From
              requirement analysis to PLC programming, SCADA integration, and
              system optimization, our workflow ensures seamless automation of
              industrial processes. This helps businesses improve productivity,
              enable real-time monitoring, reduce downtime, and achieve smart
              factory and Industry 4.0 transformation.
            </p>
          </div>

          <div className="row g-4 text-center justify-content-center">
            {STEPS.map((step, i) => (
              <div
                key={i}
                className="col-12 col-md-4 col-lg position-relative"
                data-aos="fade-up"
                data-aos-delay={`${i * 120}`}
              >
                {/* connector */}
                {i < STEPS.length - 1 && (
                  <div className="process-line d-none d-lg-block"></div>
                )}

                {/* circle */}
                <div className="process-circle mx-auto mb-3 d-flex align-items-center justify-content-center position-relative">
                  <i className={`fa-solid ${step.icon} text-red fs-5`}></i>

                  <span className="badge bg-red position-absolute process-number">
                    {i + 1}
                  </span>
                </div>

                <h5 className="fw-bold small mb-1">{step.title}</h5>

                <p className="text-muted small mb-0">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          5. FAQ
      ══════════════════════════════════════════════════════════════ */}
      <section className="margin_top pb-5 rs-bg-alt" id="faq">
        <div className="container container_width py-5">
          <div className="text-center rs-anim mb-1" data-aos="fade-up">
            <SectionLabel text="FAQ" />
          </div>
          <div
            className="text-center rs-anim mb-5"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="fw-semibold" style={{ fontSize: 20 }}>
              Industrial Automation – Frequently Asked Questions
            </h2>
            <p className="mb-3" style={{ fontSize: 15 }}>
              Everything you need to know about our Industrial Automation
              Services including PLC programming, SCADA systems, HMI
              development, IIoT integration, real-time monitoring, and smart
              factory solutions.
            </p>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-9">
              {FAQ_DATA.map(([q, a], i) => (
                <FAQItem key={i} q={q} a={a} idx={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          6. GET IN TOUCH CTA
      ══════════════════════════════════════════════════════════════ */}
      <section id="contact">
        <div className="p-5" style={{ background: "#cc0000" }}>
          <div
            className="d-flex align-items-center justify-content-center gap-3 mb-3"
            data-aos="fade-down"
          >
            <div
              className="bg-light"
              style={{ width: "40px", height: "1px" }}
            />
            <span
              className="text-uppercase fw-semibold text-white text-opacity-75"
              style={{
                letterSpacing: "2px",
                whiteSpace: "nowrap",
                fontSize: "13px",
              }}
            >
              Upgrade Your Industry with Automation
            </span>
            <div
              className="bg-light"
              style={{ width: "40px", height: "1px" }}
            />
          </div>

          <div className="row g-4 align-items-center">
            <div
              className="col-lg-7 text-center text-lg-start"
              data-aos="fade-right"
              data-aos-delay="100"
            >
              <h2 className="fw-bolder text-white text-uppercase mb-3">
                Transform Your Business with <br />
                Industrial Automation Experts
              </h2>

              <p
                className="text-white text-opacity-75 mb-0"
                style={{ lineHeight: "1.75" }}
              >
                Boost productivity and reduce downtime with our Industrial
                Automation Services. We provide PLC programming, SCADA systems,
                HMI development, and IIoT integration to enable smart factory
                operations, real-time monitoring, and efficient process control.
              </p>
            </div>

            <div
              className="col-lg-5"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-end">
                <button
                  className="btn oc-btn-white px-3 py-2 fw-bold text-uppercase"
                  onClick={() => navigate("/contact")}
                >
                  Get Free Consultation →
                </button>

                <a
                  className="btn oc-btn-ghost px-4 py-2 fw-bold text-uppercase"
                  href="tel:+918428010909"
                  aria-label="Call us for Industrial Automation Services"
                >
                  📞 +91 84280 10909
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
